import { Box, Button, type MediumIcon, Text } from '@factorialco/deprecated-design-system'

import type { InMemoryAdditionalCompensation } from 'modules/contracts/lib/contractFlowContext'
import { usePayrollConceptCompensationUnits } from 'modules/payroll_bundle/hooks/usePayrollConceptCompensationUnits'

import { AmountStrategyTag } from './AmountStrategyTag'

interface ActionStyle {
  label: string
  icon?: false | MediumIcon
}

const EditActionStyle: ActionStyle = {
  label: 'edit',
  icon: 'pencil',
}

const DeleteActionStyle: ActionStyle = {
  label: 'delete',
  icon: 'delete',
}

const ReadOnlyActionStyle: ActionStyle = {
  label: 'readOnly',
  icon: 'eye-visible',
}

type AdditionalCompensationProps = InMemoryAdditionalCompensation & {
  onEdit?: (valueToEdit: InMemoryAdditionalCompensation) => void
  onDelete?: (valueToEdit: InMemoryAdditionalCompensation) => void
  onRead?: (valueToEdit: InMemoryAdditionalCompensation) => void
}
function AdditionalCompensationCard({
  onEdit,
  onDelete,
  onRead,
  ...additionalCompensation
}: AdditionalCompensationProps) {
  const { amountStrategy, amountStrategyType, payrollConcept, recurrence } = additionalCompensation

  const unit = usePayrollConceptCompensationUnits(payrollConcept?.unitType ?? 'money')

  const actions = []
  if (onEdit) {
    actions.push({
      ...EditActionStyle,
      onClick: () => onEdit(additionalCompensation),
    })
  }
  if (onDelete) {
    actions.push({
      ...DeleteActionStyle,
      onClick: () => onDelete(additionalCompensation),
    })
  }
  if (onRead) {
    actions.push({
      ...ReadOnlyActionStyle,
      onClick: () => onRead(additionalCompensation),
    })
  }
  return (
    <Box
      paddingY="s16"
      paddingX="s24"
      gap="s8"
      flexDirection="column"
      border={{
        all: {
          width: 's1',
          color: 'grey400',
          style: 'solid',
        },
      }}
      borderRadius={{
        all: 'abs016',
      }}
      maxHeight="s96"
    >
      <Box flexDirection="row" justifyContent="spaceBetween" height="s96">
        <Box gap="s8" justifyContent="center">
          <Box flexDirection="row" alignItems="center" gap="s8">
            <Text.Normal weight="semibold" size="300" color="grey1000">
              {payrollConcept?.label}
            </Text.Normal>
          </Box>
          <Box flexDirection="row" gap="s4" alignItems="center">
            {amountStrategy && payrollConcept ? (
              <Text.Normal weight="semibold" size="200" color="grey800">
                <AmountStrategyTag
                  amountStrategyType={amountStrategyType}
                  amountStrategy={amountStrategy}
                  unit={unit}
                />
              </Text.Normal>
            ) : null}
            {recurrence && <Recurrence recurrence={recurrence} />}
          </Box>
        </Box>
        <Box gap="s8" flexDirection="row">
          {actions.map((action) => (
            <Button
              key={action.label}
              type="secondary"
              iconName={action.icon}
              onClick={action.onClick}
              aria-label={action.label}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}

// This will be a string union (factorial enumeration) but will be addressed in a subsequent PR.
type RecurrenceProps = {
  recurrence: string
}

function Recurrence({ recurrence }: RecurrenceProps) {
  const formatRecurrence = (recurrence: string) => {
    return recurrence
      .replace(/_/g, ' ')
      .replace(/\b(\d+)\b/, (match) => ` ${match} `)
      .trim()
  }

  const HelperText = ({ recurrence }: { recurrence: string }) => {
    return (
      <Text.Normal size="100" weight="medium" capitalize>
        {formatRecurrence(recurrence)}
      </Text.Normal>
    )
  }

  return <HelperText recurrence={recurrence} />
}

export { AdditionalCompensationCard }
